import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, SOCIALS } from "@/content";

const SOCIAL_ITEMS = [
  { key: "spotify", label: "Spotify", slug: "spotify", url: SOCIALS.spotify },
  { key: "apple", label: "Apple Music", slug: "applemusic", url: SOCIALS.apple },
  { key: "youtube", label: "YouTube", slug: "youtube", url: SOCIALS.youtube },
  { key: "instagram", label: "Instagram", slug: "instagram", url: SOCIALS.instagram },
  { key: "facebook", label: "Facebook", slug: "facebook", url: SOCIALS.facebook },
].filter((item) => item.url);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dark = scrolled || open;
  const iconColor = dark ? "090909" : "F4F0E8";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,color] duration-500 ${
        dark
          ? "bg-paper/90 backdrop-blur-xl border-b border-ink/10 text-ink"
          : "bg-transparent border-b border-transparent text-paper"
      }`}
    >
      <div className="relative w-full px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        {/* Left — links (desktop) / spacer (mobile) */}
        <nav aria-label="Primary" className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className="text-xs font-medium tracking-[0.18em] uppercase opacity-80 hover:opacity-100 transition-opacity duration-300"
            >
              {link.label}
            </a>
          ))}
        </nav>
        <span className="lg:hidden w-8" aria-hidden="true" />

        {/* Center — wordmark */}
        <a
          href="#top"
          data-testid="header-wordmark"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-spartan font-extrabold tracking-tight uppercase text-base md:text-lg leading-none whitespace-nowrap"
        >
          David Rosenblum
        </a>

        {/* Right — socials (desktop) / menu (mobile) */}
        <div className="hidden lg:flex items-center gap-5">
          {SOCIAL_ITEMS.map((item) => (
            <a
              key={item.key}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={item.label}
              data-testid={`header-social-${item.key}`}
              className="opacity-80 hover:opacity-100 transition-opacity duration-300"
            >
              <img
                src={`https://cdn.simpleicons.org/${item.slug}/${iconColor}`}
                alt=""
                aria-hidden="true"
                className="w-[18px] h-[18px]"
              />
            </a>
          ))}
        </div>
        <button
          type="button"
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 -mr-2"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <nav
          aria-label="Mobile"
          data-testid="mobile-menu"
          className="lg:hidden bg-paper text-ink border-t border-ink/10 px-6 py-6 flex flex-col gap-5"
        >
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
              onClick={() => setOpen(false)}
              className="font-spartan font-bold uppercase text-2xl tracking-tight"
            >
              {link.label}
            </a>
          ))}
          <div className="mt-2 flex items-center gap-5">
            {SOCIAL_ITEMS.map((item) => (
              <a
                key={item.key}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={item.label}
                data-testid={`mobile-social-${item.key}`}
                className="opacity-80 hover:opacity-100 transition-opacity duration-300"
              >
                <img
                  src={`https://cdn.simpleicons.org/${item.slug}/090909`}
                  alt=""
                  aria-hidden="true"
                  className="w-5 h-5"
                />
              </a>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}
